/**
 * The "Doesn't add up · N" toolbar chip (trust audit Phase 3; mockup
 * https://claude.ai/artifact/FK9nvDVVgAiD4xJX1grqWJ, "The toolbar chip").
 *
 * Same rule as Inbox and Tasks: a chip counts only what it owns. It counts
 * only the viewer's own tabs (Darcy → Office, Regina → Money, Corbin → Pay).
 * Gray at 0, orange at 1 or more; a failed read shows "—", never a 0. Hover
 * (or tap) shows the five oldest; "See all" opens the page. Nothing on the
 * bell and nothing to anyone's phone. Hidden for anyone the switch is off for.
 */
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { X } from "lucide-react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { InfoButton } from "@/components/info-button";
import { MISMATCH_HELP } from "./mismatch-help";
import { MISMATCH_CHIP_KEY, ageWords, checkedWords, chipState, type MismatchChipData } from "@/lib/mismatch";

/** Two short lines vs one long one: the "doesn't equal" mark. */
function MismatchIcon() {
    return (
        <svg
            viewBox="0 0 16 16"
            aria-hidden
            className="h-4 w-4 shrink-0"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.7}
            strokeLinecap="round"
        >
            <path d="M2.5 5h5M2.5 11h5M10 3.5l3.5 3.5M13.5 3.5L10 7M9.5 11h4.5" />
        </svg>
    );
}

export function MismatchChip() {
    const navigate = useNavigate();
    const q = useQuery({
        queryKey: MISMATCH_CHIP_KEY,
        queryFn: () => api.get<MismatchChipData>("/api/mismatch/chip"),
        refetchInterval: 5 * 60_000,
        refetchOnWindowFocus: true,
        staleTime: 60_000,
        retry: false,
    });
    const state = chipState(q.data, q.isError);
    const [open, setOpen] = useState(false);
    const wrapRef = useRef<HTMLDivElement>(null);
    const popRef = useRef<HTMLDivElement>(null);
    const btnRef = useRef<HTMLButtonElement>(null);
    const [anchor, setAnchor] = useState<{ top: number; right: number } | null>(null);
    const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
        const onDown = (e: MouseEvent) => {
            const t = e.target as Node;
            if (wrapRef.current?.contains(t) || popRef.current?.contains(t)) return;
            setOpen(false);
        };
        const r = btnRef.current?.getBoundingClientRect();
        if (r) setAnchor({ top: r.bottom + 6, right: Math.max(8, window.innerWidth - r.right) });
        document.addEventListener("keydown", onKey);
        document.addEventListener("mousedown", onDown);
        return () => {
            document.removeEventListener("keydown", onKey);
            document.removeEventListener("mousedown", onDown);
        };
    }, [open]);

    if (!state.show) return null;
    const data = q.data?.on ? q.data : null;
    const hoverable = () => typeof window !== "undefined" && window.matchMedia?.("(hover: hover)").matches;
    const enter = () => {
        if (hoverTimer.current) clearTimeout(hoverTimer.current);
        if (hoverable()) hoverTimer.current = setTimeout(() => setOpen(true), 150);
    };
    const leave = () => {
        if (hoverTimer.current) clearTimeout(hoverTimer.current);
        if (hoverable()) hoverTimer.current = setTimeout(() => setOpen(false), 300);
    };
    const go = (tab?: string) => {
        setOpen(false);
        navigate(tab ? `/doesnt-add-up?tab=${tab}` : "/doesnt-add-up");
    };

    return (
        <div ref={wrapRef} className="relative" onMouseEnter={enter} onMouseLeave={leave}>
            <button
                ref={btnRef}
                type="button"
                data-testid="mismatch-chip"
                data-hot={state.hot ? "true" : "false"}
                aria-label={state.label}
                aria-expanded={open}
                aria-haspopup="dialog"
                onClick={() => setOpen((v) => !v)}
                className={cn(
                    "door",
                    state.hot
                        ? "!border-orange-300 !bg-orange-50 !text-orange-900 dark:!border-orange-500/50 dark:!bg-orange-500/10 dark:!text-orange-200"
                        : "!text-muted-foreground",
                )}
            >
                <MismatchIcon />
                <span className="hidden sm:inline">Doesn't add up</span>
                {state.n === null ? (
                    <span aria-hidden>—</span>
                ) : (
                    <span
                        className={cn(
                            "inline-flex h-6 min-w-6 items-center justify-center rounded-full px-2 text-xs font-extrabold tabular-nums text-white",
                            state.hot ? "bg-orange-700" : "bg-stone-400",
                        )}
                    >
                        {state.n}
                    </span>
                )}
            </button>
            {open &&
                data &&
                createPortal(
                    <div
                        ref={popRef}
                        role="dialog"
                        aria-label="Doesn't add up, yours"
                        data-testid="mismatch-peek"
                        onMouseEnter={enter}
                        onMouseLeave={leave}
                        style={
                            anchor && window.innerWidth >= 640 ? { top: anchor.top, right: anchor.right } : undefined
                        }
                        className="fixed inset-x-2 top-16 z-[60] overflow-hidden rounded-xl border border-border bg-card text-foreground shadow-2xl sm:inset-x-auto sm:w-[400px]"
                    >
                        <div className="flex items-center justify-between gap-2 px-4 pb-2 pt-3">
                            <b className="text-[12px] font-bold uppercase tracking-wider text-muted-foreground">
                                Yours, oldest first
                            </b>
                            <span className="flex items-center gap-2">
                                <span className="text-xs text-muted-foreground">{checkedWords(data.checkedAt)}</span>
                                <InfoButton topic={MISMATCH_HELP} />
                                <button
                                    type="button"
                                    aria-label="Close"
                                    onClick={() => setOpen(false)}
                                    className="rounded p-1 hover:bg-muted sm:hidden"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            </span>
                        </div>
                        {data.oldest.length === 0 ? (
                            <div className="border-t px-4 py-4 text-sm font-semibold text-emerald-800 dark:text-emerald-300">
                                ✓ Everything of yours adds up.
                            </div>
                        ) : (
                            <ul>
                                {data.oldest.map((o) => (
                                    <li key={o.id} className="border-t">
                                        <button
                                            type="button"
                                            onClick={() => go(o.owner)}
                                            className="grid w-full grid-cols-[10px_minmax(0,1fr)_auto] items-center gap-2 px-4 py-2 text-left text-[13px] hover:bg-muted/60"
                                        >
                                            <span
                                                aria-hidden
                                                className={cn(
                                                    "h-2.5 w-2.5 rounded-full",
                                                    o.severity === "red" ? "bg-red-700" : "bg-amber-700",
                                                )}
                                            />
                                            <span className="truncate">{o.text}</span>
                                            <span className="text-xs tabular-nums text-muted-foreground">
                                                {ageWords(o.ageDays)}
                                            </span>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                        <div className="flex justify-end border-t bg-muted/50 px-4 py-2.5 text-xs">
                            <button
                                type="button"
                                onClick={() => go(data.tabs[0])}
                                className="font-bold text-[#7A5A0A] hover:underline dark:text-amber-300"
                            >
                                See all {data.count} →
                            </button>
                        </div>
                    </div>,
                    document.body,
                )}
        </div>
    );
}
